// app.fn transport: invoke a project's per-function Lambda
// (`s3p-{slug}-fn-{name}`) directly via the Lambda API, signed with the caller's
// STS creds (IAM gates it via `lambda:InvokeFunction` on `s3p-{slug}-fn-*`).
//
//   - enqueue() → InvocationType "Event" (async, fire-and-forget): Lambda's own
//     async queue + retries apply; the handler writes data/outputs/{taskId}.json,
//     which the RPC layer polls.
//   - invoke() → InvocationType "RequestResponse" (sync): returns the result
//     inline (for quick functions / inter-call use).
//
// WHO THE CALLER IS. Signing the invocation with STS proves only that we're
// *some* member of this project — IAM gates the function ARN, and every member
// shares one role. It says nothing about WHICH member, and a Lambda event
// carries no verified principal. So we ship the caller's **id_token** and the
// runtime verifies it (RS256 vs the pool JWKS) — see verify-id-token.ts.
//
// Do NOT go back to sending bare claims. An earlier version passed a plain
// `enqueuedBy: {sub, groups}` object lifted from the local token, which any
// signed-in user could edit before it left the browser: forge a `sub`, and
// send-email's per-user daily quota (keyed on it) evaporates.
//
// (The deploy also gives each function a Function URL for HTTP/inter-lambda use;
// the browser uses the Lambda API directly — no hand-rolled SigV4.)

import {
  InvokeCommand,
  LambdaClient,
} from "https://cdn.jsdelivr.net/npm/@aws-sdk/client-lambda@3.1054.0/+esm";
import type { JwtClaims } from "./oidc.ts";

export interface InvokerConfig {
  region: string;
  slug: string;
  getCredentials: () => Promise<{
    accessKeyId: string;
    secretAccessKey: string;
    sessionToken?: string;
  }>;
  /** The caller's raw Cognito id_token — the ONLY thing the runtime will accept
   *  as proof of identity. Null when signed out (the handler then sees an empty
   *  `ctx.caller` and fails closed if it needs one). */
  getIdToken: () => string | null;
}

export interface Invoker {
  /** Function name `s3p-{slug}-fn-{name}`. */
  fnName(name: string): string;
  /** Async fire-and-forget — the handler writes the sub-scoped rpc output. */
  enqueue(name: string, input: unknown, taskId: string): Promise<void>;
  /** Sync — returns the handler's result inline (throws on handler error). */
  invoke(name: string, input: unknown): Promise<unknown>;
}

export function createInvoker(cfg: InvokerConfig): Invoker {
  async function client(): Promise<LambdaClient> {
    const c = await cfg.getCredentials();
    return new LambdaClient({
      region: cfg.region,
      credentials: {
        accessKeyId: c.accessKeyId,
        secretAccessKey: c.secretAccessKey,
        sessionToken: c.sessionToken,
      },
    });
  }
  const fnName = (name: string) => `s3p-${cfg.slug}-fn-${name}`;
  const encode = (o: unknown) => new TextEncoder().encode(JSON.stringify(o));

  return {
    fnName,
    async enqueue(name, input, taskId) {
      const l = await client();
      await l.send(
        new InvokeCommand({
          FunctionName: fnName(name),
          InvocationType: "Event",
          Payload: encode({ taskId, input, idToken: cfg.getIdToken() ?? undefined }),
        }),
      );
    },
    async invoke(name, input) {
      const l = await client();
      const res = await l.send(
        new InvokeCommand({
          FunctionName: fnName(name),
          InvocationType: "RequestResponse",
          Payload: encode({ input, idToken: cfg.getIdToken() ?? undefined }),
        }),
      );
      const text = res.Payload ? new TextDecoder().decode(res.Payload as Uint8Array) : "";
      if (res.FunctionError) throw new Error(`function '${name}' failed: ${text}`);
      const out = JSON.parse(text || "{}") as {
        status?: string;
        result?: unknown;
        error?: { message?: string };
      };
      if (out.status === "error") throw new Error(out.error?.message ?? `function '${name}' error`);
      return out.result;
    },
  };
}
